/**
 * useWorkflows — thin client for the /api/workflows surface.
 *
 * Exposes:
 *   templates, workflows, runs, isLoading, error
 *   listTemplates(), listWorkflows(), listRuns()
 *   generate(description, language)
 *   getWorkflow(id)
 *   runWorkflow(id, input, language)
 *   getRun(runId)
 *   resumeRun(runId, approval)
 *
 * Auth header is attached automatically when an AuthProvider session is
 * present; anonymous requests are bucketed server-side under
 * user_id="anonymous".
 */
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getBackendBaseUrl } from '@/lib/backendUrl';
import { safeFetch } from '@/lib/safeFetch';

const BACKEND_URL = getBackendBaseUrl();
const ROOT = `${BACKEND_URL}/api/workflows`;

export const useWorkflows = () => {
  const { session } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [runs, setRuns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const headers = useCallback((extra = {}) => {
    const h = { 'Content-Type': 'application/json', ...extra };
    if (session?.access_token) h.Authorization = `Bearer ${session.access_token}`;
    return h;
  }, [session]);

  const _safe = useCallback(async (fn) => {
    setError(null);
    try {
      return await fn();
    } catch (e) {
      setError(e?.message || 'Network error');
      throw e;
    }
  }, []);

  const listTemplates = useCallback(async () => {
    setIsLoading(true);
    try {
      return await _safe(async () => {
        const res = await fetch(`${ROOT}/templates`, { headers: headers() });
        if (!res.ok) throw new Error(`templates ${res.status}`);
        const data = await res.json();
        setTemplates(data.templates || []);
        return data.templates || [];
      });
    } finally {
      setIsLoading(false);
    }
  }, [_safe, headers]);

  const listWorkflows = useCallback(async () => {
    return _safe(async () => {
      const res = await fetch(`${ROOT}`, { headers: headers() });
      if (!res.ok) throw new Error(`list ${res.status}`);
      const data = await res.json();
      setWorkflows(data.workflows || []);
      return data.workflows || [];
    });
  }, [_safe, headers]);

  const listRuns = useCallback(async () => {
    return _safe(async () => {
      const res = await fetch(`${ROOT}/runs?limit=25`, { headers: headers() });
      if (!res.ok) throw new Error(`runs ${res.status}`);
      const data = await res.json();
      setRuns(data.runs || []);
      return data.runs || [];
    });
  }, [_safe, headers]);

  const generate = useCallback(async (description, language = 'ar') => {
    setIsLoading(true);
    try {
      return await _safe(async () => {
        const res = await fetch(`${ROOT}/generate`, {
          method: 'POST',
          headers: headers(),
          body: JSON.stringify({ description, language }),
        });
        if (!res.ok) throw new Error(`generate ${res.status}`);
        const data = await res.json();
        return data.workflow;
      });
    } finally {
      setIsLoading(false);
    }
  }, [_safe, headers]);

  const getWorkflow = useCallback(async (id) => {
    return _safe(async () => {
      const res = await fetch(`${ROOT}/${encodeURIComponent(id)}`, { headers: headers() });
      if (!res.ok) throw new Error(`get ${res.status}`);
      const data = await res.json();
      return data.workflow;
    });
  }, [_safe, headers]);

  const runWorkflow = useCallback(async (id, input = {}, language = 'ar') => {
    setIsLoading(true);
    try {
      return await _safe(async () => {
        const res = await fetch(`${ROOT}/${encodeURIComponent(id)}/run`, {
          method: 'POST',
          headers: headers(),
          body: JSON.stringify({ input, language }),
        });
        if (!res.ok) throw new Error(`run ${res.status}`);
        const data = await res.json();
        return data.run;
      });
    } finally {
      setIsLoading(false);
    }
  }, [_safe, headers]);

  /**
   * Streaming run — POSTs to /run/stream and yields SSE events to the
   * provided callbacks until [DONE] or workflow_failed.
   *
   *   onEvent({event, ...payload}) — every parsed event
   *   returns: a `cancel()` function the caller can invoke to abort the
   *            client-side reader. The server keeps running in the
   *            background; final state is always available via getRun().
   */
  const runWorkflowStream = useCallback(async (id, input = {}, language = 'ar', onEvent) => {
    const ctrl = new AbortController();
    const url = `${ROOT}/${encodeURIComponent(id)}/run/stream`;
    const res = await fetch(url, {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({ input, language }),
      signal: ctrl.signal,
    });
    if (!res.ok || !res.body) {
      throw new Error(`run/stream ${res.status}`);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    (async () => {
      try {
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          // Split on SSE frame boundary (\n\n)
          let idx;
          while ((idx = buffer.indexOf('\n\n')) !== -1) {
            const frame = buffer.slice(0, idx).trim();
            buffer = buffer.slice(idx + 2);
            if (!frame.startsWith('data:')) continue;
            const data = frame.slice(5).trim();
            if (data === '[DONE]') { onEvent?.({ event: 'done' }); return; }
            try {
              const parsed = JSON.parse(data);
              onEvent?.(parsed);
            } catch {
              /* ignore malformed frame */
            }
          }
        }
        onEvent?.({ event: 'done' });
      } catch (e) {
        if (e.name !== 'AbortError') onEvent?.({ event: 'error', error: e.message });
      }
    })();

    return () => ctrl.abort();
  }, [headers]);

  const getRun = useCallback(async (runId) => {
    return _safe(async () => {
      const res = await fetch(`${ROOT}/runs/${encodeURIComponent(runId)}`, { headers: headers() });
      if (!res.ok) throw new Error(`run-state ${res.status}`);
      const data = await res.json();
      return data.run;
    });
  }, [_safe, headers]);

  const resumeRun = useCallback(async (runId, approval = {}) => {
    setIsLoading(true);
    try {
      return await _safe(async () => {
        const res = await fetch(`${ROOT}/runs/${encodeURIComponent(runId)}/resume`, {
          method: 'POST',
          headers: headers(),
          body: JSON.stringify({ approval }),
        });
        if (!res.ok) throw new Error(`resume ${res.status}`);
        const data = await res.json();
        return data.run;
      });
    } finally {
      setIsLoading(false);
    }
  }, [_safe, headers]);

  const saveWorkflow = useCallback(async (workflow) => {
    setIsLoading(true);
    try {
      return await _safe(async () => {
        const res = await fetch(`${ROOT}/`, {
          method: 'POST', headers: headers(),
          body: JSON.stringify(workflow),
        });
        if (!res.ok) throw new Error(`save ${res.status}`);
        const data = await res.json();
        return data.workflow;
      });
    } finally {
      setIsLoading(false);
    }
  }, [_safe, headers]);

  const duplicateWorkflow = useCallback(async (id, name = null, name_ar = null) => {
    setIsLoading(true);
    try {
      return await _safe(async () => {
        const res = await fetch(`${ROOT}/${encodeURIComponent(id)}/duplicate`, {
          method: 'POST', headers: headers(),
          body: JSON.stringify({ name, name_ar }),
        });
        if (!res.ok) throw new Error(`duplicate ${res.status}`);
        const data = await res.json();
        return data.workflow;
      });
    } finally {
      setIsLoading(false);
    }
  }, [_safe, headers]);

  const improveWorkflow = useCallback(async (id, feedback = '') => {
    setIsLoading(true);
    try {
      return await _safe(async () => {
        const res = await fetch(`${ROOT}/${encodeURIComponent(id)}/improve`, {
          method: 'POST', headers: headers(),
          body: JSON.stringify({ feedback }),
        });
        if (!res.ok) throw new Error(`improve ${res.status}`);
        return await res.json();   // {workflow, improvement_notes, run_summary}
      });
    } finally {
      setIsLoading(false);
    }
  }, [_safe, headers]);

  const renderDocument = useCallback(async ({ content, format, title = '', language = 'ar', metadata = {} }) => {
    return _safe(async () => {
      // safeFetch bypasses the Emergent platform's window.fetch wrapper,
      // which drains response bodies on any non-2xx and would otherwise
      // turn a 4xx/5xx render failure into "body stream already read"
      // before we can surface a useful toast to the user.
      const res = await safeFetch(`${BACKEND_URL}/api/documents/render`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ content, format, title, language, metadata }),
      });
      if (!res.ok) throw new Error(`render ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safe = (title || 'document').replace(/[^\w\u0600-\u06FF\s\-]/g, '').trim().replace(/\s+/g, '_') || 'document';
      a.download = `${safe}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      return true;
    });
  }, [_safe, headers]);

  // Initial load
  useEffect(() => {
    listTemplates().catch(() => {});
    listRuns().catch(() => {});
  // Cluster G.C (Feb 2026): mount-once init. `listTemplates` /
  // `listRuns` are useCallbacks that rebuild each render; including
  // them in deps would re-fire the load loop. Empty-deps mount-once
  // is the intended behaviour.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    templates, workflows, runs, isLoading, error,
    listTemplates, listWorkflows, listRuns,
    generate, getWorkflow, runWorkflow, runWorkflowStream, getRun, resumeRun,
    saveWorkflow, duplicateWorkflow, improveWorkflow, renderDocument,
  };
};

export default useWorkflows;
